import { Booking } from '../../core/models/booking';
import { SessionType } from '../adapters/luminara/session-type/dto/session-type.dto';
import { AnalyticsClient } from './analytics-client';
import { MixpanelAnalyticsClient } from './mixpanel/mixpanel-analytics-client-implementation';

class AnalyticsEventUtil {
  private static instance: AnalyticsEventUtil | null = null;
  private client: AnalyticsClient;

  private constructor() {
    this.client = new MixpanelAnalyticsClient(); // Initialize your Mixpanel client here
  }

  public static getInstance(): AnalyticsEventUtil {
    if (!this.instance) {
      this.instance = new AnalyticsEventUtil();
    }
    return this.instance;
  }

  identify(id?: string): void {
    this.client.identifyUser(id);
  }
  sessionStarted(booking?: Booking) {
    this.client.trackEvent('Session Started', { session: booking });
  }

  joinedHeallo(bookingId: string, isHealer: boolean) {
    this.client.trackEvent('Joined Heallo', { bookingId, isHealer });
  }

  colorPicked(color?: any) {
    this.client.trackEvent('Heallo - Color Picked', { color });
  }

  startedTouchingHeallo() {
    this.client.trackEvent('Heallo - Started touching', {});
  }

  stoppedTouchingHeallo() {
    this.client.trackEvent('Heallo - Stopped touching', {});
  }

  arrivedOnMeetingScreen(bookingId: string) {
    this.client.trackEvent('Arrived on Meeting screen', { bookingId });
  }

  dyteMeetingInitialized(meeting: any, bookingId: string) {
    this.client.trackEvent('Dyte Meeting App initialized', { meeting, bookingId });
  }

  backgroundBlurAdded(bookingId: string) {
    this.client.trackEvent('Background Blur successfully added', { bookingId });
  }
  sessionMarkedAsNoShow(bookingId: string, isHealer: boolean) {
    this.client.trackEvent('Session marked as NO SHOW', { bookingId, isHealer });
  }
  sessionFinished(bookingId: string, isHealer: boolean) {
    this.client.trackEvent('Session finished', { bookingId, isHealer });
  }
  healerClickedFinishSessionBtn() {
    this.client.trackEvent('Healer clicked "Finish Session" button', {});
  }
  clientClickedLeaveSessionBtn() {
    this.client.trackEvent('Client clicked "Leave room" button', {});
  }
  startSessionButtonClicked(bookingId?: string) {
    this.client.trackEvent('"Start Session" button clicked', { bookingId });
  }
  rejoinSessionButtonClicked(bookingId?: string) {
    this.client.trackEvent('"Rejoin Session" button clicked', { bookingId });
  }
  errorInHealerDashboard(errors: any) {
    this.client.trackEvent('ERROR: Loading data error in Healer Dashboard', { errors });
  }
  errorInClientDashboard(errors: any) {
    this.client.trackEvent('ERROR: Loading data error in Client Dashboard', { errors });
  }
  setSoundtrackVolume(volume: number) {
    this.client.trackEvent('Set session music volume', { volume });
  }
  healloInvitationDisplayed(bookingId: string) {
    this.client.trackEvent('Heallo Invitation displayed', { bookingId });
  }
  clientInWaitingRoom() {
    this.client.trackEvent('Client in waiting room', {});
  }
  audioEnableButtonClicked() {
    this.client.trackEvent('Mic enable button clicked', {});
  }
  audioDisableButtonClicked() {
    this.client.trackEvent('Mic disable button clicked', {});
  }
  videoEnableButtonClicked() {
    this.client.trackEvent('Camera enable button clicked', {});
  }
  videoDisableButtonClicked() {
    this.client.trackEvent('Camera disable button clicked', {});
  }
  meetingStarted(bookingId: string) {
    this.client.trackEvent('Meeting started with both participants present', { bookingId });
  }

  invitationLinkCopied(invitationLink: string) {
    this.client.trackEvent('Invitation link copied', { invitationLink });
  }

  redirectingHealerToFeedbackScreen(bookingId: string) {
    this.client.trackEvent('Healer redirected to Feedback screen', { bookingId });
  }

  websocketDisconnected() {
    this.client.trackEvent('ERROR: Websocket disconnected', {});
  }
  websocketChangeState(state: any) {
    this.client.trackEvent('Websocket state change', { state });
  }

  websocketClosed(event: any) {
    this.client.trackEvent('ERROR: Websocket closed', { event });
  }
  websocketError(event: any) {
    this.client.trackEvent('ERROR: Websocket error', { event });
  }
  websocketConnected() {
    this.client.trackEvent('Websocket connected', {});
  }
  websocketMsgError(msg: any, error: any) {
    this.client.trackEvent('ERROR: Websocket message error', { msg, error });
  }

  scheduleBookingAsHealerSuccess(bookedTime: any, firstName: any, lastName: any, email: any, sessionTypeId: string) {
    this.client.trackEvent('Healer scheduling booking for client successful', { bookedTime, firstName, lastName, email, sessionTypeId });
  }
  scheduleBookingAsHealerFailure(bookedTime: any, firstName: any, lastName: any, email: any, sessionTypeId: string, error: any) {
    this.client.trackEvent('ERROR: Healer scheduling for  client failure', { bookedTime, firstName, lastName, email, sessionTypeId, error });
  }
  scheduleBookingAsClientSuccess(healerUserProfileId: string, clientUserProfileId: string, bookedTimeTimestamp: number, bookingId: string) {
    this.client.trackEvent('Client scheduling booking successful', { healerUserProfileId, clientUserProfileId, bookedTimeTimestamp, bookingId });
  }
  scheduleBookingAsClientFailure(healerUserProfileId: string, clientUserProfileId: string, bookedTimeTimestamp: number, error: any) {
    this.client.trackEvent('ERROR: Client scheduling booking failure', { healerUserProfileId, clientUserProfileId, bookedTimeTimestamp, error });
  }

  joinSessionMeetingButtonClicked() {
    this.client.trackEvent('"Join session" button clicked in Preview Camera screen', {});
  }
  windowResized(height: number, width: number) {
    this.client.trackEvent('Window size changed', { height, width });
  }
  joinHealloInvitationClicked(bookingId: string) {
    this.client.trackEvent('Joining heallo from Pre-session invitation', { bookingId });
  }
  healloInProgress(bookingId: string) {
    this.client.trackEvent('Heallo - In Progress', { bookingId });
  }
  healloFinished(bookingId: string) {
    this.client.trackEvent('Heallo - Finished', { bookingId });
  }
  bothParticipantsPressingAtSameTime() {
    this.client.trackEvent('Heallo - Both participants pressing at same time', {});
  }
  async mediaDevicesFetched(audioDevices$: Promise<any>, videoDevices$: Promise<any>, speakerDevices$: Promise<any>, currentDevices: any) {
    Promise.all([audioDevices$, videoDevices$, speakerDevices$]).then(([audioDevices, videoDevices, speakerDevices]) => {
      this.client.trackEvent('Media devices fetched', {
        audioDevices: JSON.parse(JSON.stringify(audioDevices)),
        videoDevices: JSON.parse(JSON.stringify(videoDevices)),
        speakerDevices: JSON.parse(JSON.stringify(speakerDevices)),
        currentDevices: JSON.parse(JSON.stringify(currentDevices)),
      });
    });
  }

  soundtrackLoaded(soundtrack: string) {
    this.client.trackEvent('Soundtrack loaded', { soundtrack });
  }

  soundtrackVolumeAdjusted(volume: number) {
    this.client.trackEvent('Soundtrack volume adjusted', { volume });
  }

  musicEnabled() {
    this.client.trackEvent('Music turned on', {});
  }

  musicDisabled() {
    this.client.trackEvent('Music turned off', {});
  }
  musicPlayingError(error: any) {
    this.client.trackEvent('Music turned off', { error });
  }
  soundIconClicked(wasMusicOn: boolean) {
    this.client.trackEvent(`Sound icon clicked - turning music ${wasMusicOn ? 'OFF' : 'ON'}`, {});
  }
  healloUnsubscribed(subscriptionId: string) {
    this.client.trackEvent(`Unsubscribed from Heallo`, { subscriptionId });
  }
  healloSubscribed(subscriptionId: string) {
    this.client.trackEvent(`Subscribed to Heallo`, { subscriptionId });
  }
  waitingForHealloSubscription(destination: string) {
    this.client.trackEvent(`Waiting for Heallo subscription`, { destination });
  }
  availabilityUpdated(availability: any) {
    this.client.trackEvent('Healer availability updated', { availability });
  }
  availabilityUpdateFailure(error: any) {
    this.client.trackEvent('ERROR: Healer availability update failed ', { error });
  }
  connectCalendarClicked() {
    this.client.trackEvent('Healer clicked "Connect Calendar" or "Replace Calendar"', {});
  }
  connectCalendarAuthorizationUrlError(err: any) {
    this.client.trackEvent('ERROR: Failed fetching authorization URL for connecting calendar"', { err });
  }
  calendarConnectedSuccessfully() {
    this.client.trackEvent('Healer connected calendar successfully', {});
  }
  calendarConnectingFailure(error: any) {
    this.client.trackEvent('ERROR: Healer failed connecting calendar', { error });
  }
  healerSchedulingUrlFetchedSuccessfully(healerUserProfileId: string, bookingUrl: string) {
    this.client.trackEvent('Healer scheduling URL fetched successfully', { healerUserProfileId, bookingUrl });
  }
  healerSchedulingUrlFetchingFailed(healerUserProfileId: string, error: any) {
    this.client.trackEvent('ERROR: Healer scheduling URL fetching failure', { healerUserProfileId, error });
  }

  reschedulingAsClientSucceeded(bookingId: string) {
    this.client.trackEvent('Rescheduling as client successful!', { bookingId });
  }

  reschedulingAsClientFailed(error: any) {
    this.client.trackEvent('ERROR: Rescheduling as client failed!', { error });
  }

  cancelingBookingAsHealerSuccess(bookingId: string) {
    this.client.trackEvent('Canceling booking as healer successful!', { bookingId });
  }

  cancelingBookingAsHealerFailure(error: any) {
    this.client.trackEvent('ERROR: Canceling booking as healer failed!', { error });
  }

  cancelingBookingAsClientSuccess(bookingId: string) {
    this.client.trackEvent('Canceling booking as client successful!', { bookingId });
  }

  cancelingBookingAsClientFailure(error: any) {
    this.client.trackEvent('ERROR: Canceling booking as client failed!', { error });
  }

  markingAsHealloCompletedSuccess(error: any) {
    this.client.trackEvent('Successfully marked booking as HEALLO_COMPLETED ', { error });
  }
  markingAsHealloCompletedFailed(bookingId: string, error: any) {
    this.client.trackEvent('ERROR: Marking booking as HEALLO_COMPLETED failed', { bookingId, error });
  }

  healerRecommendationFetchedSuccessfully(clientUserProfileId: string) {
    this.client.trackEvent(`Fetched healer recommendations for client successfully`, { clientUserProfileId });
  }

  healerRecommendationFetchError(clientUserProfileId: string, err: any) {
    this.client.trackEvent(`ERROR: Failed fetching healer recommendations for client`, { clientUserProfileId, err });
  }

  healerRecommendationExpired(clientUserProfileId: string) {
    this.client.trackEvent(`ERROR: Healer recommendation EXPIRED for client`, { clientUserProfileId });
  }

  allHealerRecommendationFetchedSuccessfully(clientUserProfileId: string) {
    this.client.trackEvent(`Fetched all healer recommendations for client successfully`, { clientUserProfileId });
  }

  allHealerRecommendationFetchError(clientUserProfileId: string, err: any) {
    this.client.trackEvent(`ERROR: Failed fetching all healer recommendations for client`, { clientUserProfileId, err });
  }

  skippedPaymentScreenCauseOfExistingBookingStockings(clientUserProfileId: string, packageId: string, booking: Booking) {
    this.client.trackEvent(`Client has booking stocks > 0, skipping payment screen`, {
      clientUserProfileId,
      booking,
      packageId,
    });
  }

  allSessionTypesFetched(sessionTypes: SessionType[]) {
    this.client.trackEvent('Successfully fetched all session types', { sessionTypes });
  }
  sesionTypeLoadingFailed(err: any) {
    this.client.trackEvent('ERROR: Failed loading all session types', { err });
  }

  healerPackageFetchedSuccessfully(healerPackageId: string) {
    this.client.trackEvent(`Successfully fetched healer package`, { healerPackageId });
  }
  healerPackageFetchError(healerPackageId: string, err: any) {
    this.client.trackEvent(`ERROR: Failed loading healer package info for package`, { healerPackageId, err });
  }

  redirectingToRebookScreen(clientId: string, healerId: string) {
    this.client.trackEvent(`Redirecting client to rebook screen`, { clientId, healerId });
  }

  navigateToClientDashboardClicked(clientUserProfileId: string) {
    this.client.trackEvent(`Redirecting client to client dashboard screen`, {
      clientId: clientUserProfileId,
    });
  }

  navigateToClientBookedAppointmentsScreen(clientUserProfileId: string) {
    this.client.trackEvent(`Redirecting client to BookedAppointmentScreen`, {
      clientId: clientUserProfileId,
    });
  }

  navigateToMyHealersScreen(clientUserProfileId: string) {
    this.client.trackEvent(`Redirecting client to My Healers screen`, {
      clientId: clientUserProfileId,
    });
  }

  navigateToLogin(returnUrl: string) {
    this.client.trackEvent(`Redirecting user to login screen.`, {
      returnUrl,
    });
  }

  navigateToScheduleScreen(clientUserProfileId: string, packageId: string) {
    this.client.trackEvent(`Redirecting client to Schedule Booking screen`, {
      clientId: clientUserProfileId,
      packageId,
    });
  }

  navigateToRequestNewHealerScreen() {
    this.client.trackEvent(`Redirecting client to RequestNewHealer screen`, {});
  }

  routeChanged(url: string) {
    this.client.trackEvent(`Route changed to ${url}`, {
      url,
    });
  }

  processingPaymentForSingleBooking(sessionId: string, clientUserProfileId: string, packageId: string, bookingId: string) {
    this.client.trackEvent(`Trying to process payment for single booking`, {
      bookingId,
      clientUserProfileId,
      sessionId,
      packageId,
    });
  }
  paymentProcessedSuccessfullyForSingleBooking(sessionId: string, clientUserProfileId: string, packageId: string, bookingId: string) {
    this.client.trackEvent(`Successfully processed payment for single booking`, {
      bookingId,
      clientUserProfileId,
      sessionId,
      packageId,
    });
  }

  paymentNotProcessedForSingleBooking(sessionId: string, clientUserProfileId: string, packageId: string, bookingId: string, err?: any) {
    this.client.trackEvent(`Failed processing payment for single booking`, {
      bookingId,
      clientUserProfileId,
      sessionId,
      packageId,
      err,
    });
  }
  navigateToPaymentForMultipleBookingsScreen(clientUserProfileId: string, packageId: string) {
    this.client.trackEvent(`Navigating client to Payment screen for multiple bookings`, {
      clientUserProfileId,
      packageId,
    });
  }
  processingPaymentForMultipleBookings(sessionId: string, clientUserProfileId: string, packageId: string) {
    this.client.trackEvent(`Trying to process payment for multiple bookings`, {
      clientUserProfileId,
      sessionId,
      packageId,
    });
  }
  paymentProcessedSuccessfullyForMultipleBookings(sessionId: string, clientUserProfileId: string, packageId: string) {
    this.client.trackEvent(`Successfully processed payment for multiple bookings`, {
      clientUserProfileId,
      sessionId,
      packageId,
    });
  }

  paymentNotProcessedForMultipleBookings(sessionId: string, clientUserProfileId: string, packageId: string, err?: any) {
    this.client.trackEvent(`Failed processing payment for multiple bookings`, {
      clientUserProfileId,
      sessionId,
      packageId,
      err,
    });
  }
  creatingPaymentSessionForMultipleBookings(clientUserProfileId: string, packageId: string) {
    this.client.trackEvent(`Trying to create payment session for multiple bookings for client`, {
      clientUserProfileId,
      packageId,
    });
  }
  creatingPaymentSessionForSingleBooking(clientUserProfileId: string, packageId: string, bookingId: string) {
    this.client.trackEvent(`Trying to create payment session for single booking for client`, {
      clientUserProfileId,
      packageId,
      bookingId,
    });
  }

  paymentSessionCreatedForSingleBooking(clientUserProfileId: string, packageId: string, bookingId: string) {
    this.client.trackEvent(`Payment session created for single booking for client`, {
      clientUserProfileId,
      packageId,
      bookingId,
    });
  }

  paymentSessionCreatedForMultipleBookings(clientUserProfileId: string, packageId: string) {
    this.client.trackEvent(`Payment session created for multiple bookings booking for client`, {
      clientUserProfileId,
      packageId,
    });
  }

  paymentCreationFailed(clientUserProfileId: string, packageId: string, bookingId: string, e: any) {
    this.client.trackEvent(`ERROR: Failed creation of payment session for client`, {
      clientUserProfileId,
      packageId,
      bookingId,
      e,
    });
  }

  creatingPaymentSessionForMultipleBookingsError(clientUserProfileId: string, packageId: string, error: any) {
    this.client.trackEvent(`ERROR: Failed creation of multiple bookings payment session for client`, {
      clientUserProfileId,
      packageId,
      error,
    });
  }

  navigateToHealerSelectionScreen(clientUserProfileId: string | undefined) {
    this.client.trackEvent(`Navigating client to Healer Selection screen`, {
      clientUserProfileId,
    });
  }

  bookingFetchErrorAfterSuccessfulSchedule(bookingId: string) {
    this.client.trackEvent(`ERROR: Booking fetching failed after successfully scheduling the booking`, {
      bookingId,
    });
  }
  bookingFetchedAfterSuccessfulSchedule(bookingId: string) {
    this.client.trackEvent(`Booking fetched after successful scheduling (PAID)`, {
      bookingId,
    });
  }
  navigateToBookingScheduledSuccessfullyScreen(clientUserProfileId: string, packageId: string, bookingId: string) {
    this.client.trackEvent(`Navigating client to 'Booking Scheduled Successfully' screen (PAID)`, {
      clientUserProfileId,
      packageId,
      bookingId,
    });
  }

  bookHealerButtonClicked(clientUserProfileId: string, packageId: string, isPackageForMultipleBookings: boolean) {
    this.client.trackEvent(`Client clicked 'Book Healer' button`, {
      clientUserProfileId,
      packageId,
      isPackageForMultipleBookings,
    });
  }

  paymentTimerCountdownStarted(seconds: number) {
    this.client.trackEvent(`Payment timer countdown started`, {
      seconds,
    });
  }

  errorWhileMarkingRecommendationAsOpened(recommendationId: string, error: any) {
    this.client.trackEvent(`ERROR: Failed marking healer recommendation as opened`, {
      recommendationId,
      error,
    });
  }
  recommendationMarkedAsOpenedSuccessfully(recommendationId: string) {
    this.client.trackEvent(`Successfully marked healer recommendation as opened`, {
      recommendationId,
    });
  }

  errorWhileMarkingRecommendationAsBookHealerButtonClicked(recommendationId: string, error: any) {
    this.client.trackEvent(`ERROR: Failed marking healer recommendation as book healer button clicked`, {
      recommendationId,
      error,
    });
  }
  recommendationMarkedAsBookHealerButtonClickedSuccessfully(recommendationId: string) {
    this.client.trackEvent(`Successfully marked healer recommendation as book healer button clicked`, {
      recommendationId,
    });
  }

  errorLoadingBookingData(bookingId: string, error: unknown) {
    this.client.trackEvent('ERROR: Failed loading "Booking" data', { bookingId, error });
  }

  healerPageMarkedAsOpenedSuccessfully(recommendationId: string, healerId: string) {
    this.client.trackEvent(`Successfully marked healer page as opened`, {
      recommendationId,
    });
  }
  errorMarkingHealerPageAsOpened(recommendationId: string, healerId: string, error: any) {
    this.client.trackEvent(`ERROR: Failed marking healer page as opened`, {
      recommendationId,
      error,
    });
  }
}
export default AnalyticsEventUtil;
